import { useMemo, useState } from 'react'
import { useNavigate } from '@/lib/router'
import type { ProjectListItem } from '../../api/types'
import { forceLayout } from '../../lib/forceLayout'
import { formatCrore, plural, RUPEES_PER_CRORE } from '../../lib/format'
import { relationships, type Relationship } from './chartData'
import Tooltip from './Tooltip'
import { useElementWidth } from './useElementWidth'
import { useTooltip } from './useTooltip'

const HEIGHT = 460

type Node =
  | { id: string; kind: 'person'; r: number; space: number; label: string; person: Relationship }
  | { id: string; kind: 'project'; r: number; label: string; project: ProjectListItem }

/**
 * "Relationship network": stakeholders (circles, sized by how many projects they touch)
 * linked to the active projects they influence (squares, sized by ₹ value, coloured by stage).
 * Hover or focus a node to isolate its connections; click or press Enter to open it.
 */
export default function NetworkGraph({ projects }: { projects: ProjectListItem[] }) {
  const navigate = useNavigate()
  const { ref: sizeRef, width } = useElementWidth<HTMLDivElement>()
  const { ref: tipRef, tip, bind } = useTooltip<HTMLDivElement>()
  const [focused, setFocused] = useState<string | null>(null)

  const graph = useMemo(() => {
    const people = relationships(projects)
    const linked = projects.filter((p) => p.stakeholders.length > 0)
    const nodes: Node[] = [
      ...people.map((person): Node => {
        const r = 7 + 3 * person.projects.length
        // Keep room for the name under the circle (about 3.3px per character at 11px).
        return { id: `s${person.id}`, kind: 'person', r, space: Math.max(r + 14, person.name.length * 3.3), label: person.name, person }
      }),
      ...linked.map((project): Node => ({
        id: `p${project.id}`, kind: 'project', label: project.title, project,
        r: 6 + Math.sqrt(project.valueRupees / RUPEES_PER_CRORE) * 0.55,
      })),
    ]
    const links = linked.flatMap((p) => p.stakeholders.map((s) => ({ source: `s${s.id}`, target: `p${p.id}` })))
    const neighbours = new Map<string, Set<string>>()
    for (const l of links) {
      neighbours.set(l.source, (neighbours.get(l.source) ?? new Set()).add(l.target))
      neighbours.set(l.target, (neighbours.get(l.target) ?? new Set()).add(l.source))
    }
    const positions = width > 0 ? forceLayout(nodes, links, width, HEIGHT) : new Map()
    return { nodes, links, neighbours, positions }
  }, [projects, width])

  const isLit = (id: string) => !focused || id === focused || graph.neighbours.get(focused)?.has(id)
  const open = (n: Node) => navigate(n.kind === 'person' ? `/stakeholders/${n.person.id}` : `/projects/${n.project.id}`)

  return (
    <div ref={sizeRef}>
      <div className="network" ref={tipRef} style={{ height: HEIGHT }}>
        {width > 0 && (
          <svg width={width} height={HEIGHT} role="group" aria-label="Stakeholders linked to active projects">
            <g className="network-links">
              {graph.links.map((l) => {
                const a = graph.positions.get(l.source)
                const b = graph.positions.get(l.target)
                if (!a || !b) return null
                const lit = !focused || l.source === focused || l.target === focused
                return (
                  <line key={`${l.source}-${l.target}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className={lit ? '' : 'dim'} />
                )
              })}
            </g>
            {graph.nodes.map((n) => {
              const p = graph.positions.get(n.id)
              if (!p) return null
              const tipValue =
                n.kind === 'person'
                  ? `${plural(n.person.projects.length, 'active project')} · ${formatCrore(n.person.value)}`
                  : `${formatCrore(n.project.valueRupees)} · ${n.project.stage}`
              const handlers = bind(tipValue, n.label)
              return (
                <g
                  key={n.id}
                  className={`network-node ${isLit(n.id) ? '' : 'dim'}`}
                  role="link"
                  {...handlers}
                  onPointerMove={(e) => {
                    setFocused(n.id)
                    handlers.onPointerMove(e)
                  }}
                  onPointerLeave={() => {
                    setFocused(null)
                    handlers.onPointerLeave()
                  }}
                  onFocus={(e) => {
                    setFocused(n.id)
                    handlers.onFocus(e)
                  }}
                  onBlur={() => {
                    setFocused(null)
                    handlers.onBlur()
                  }}
                  onClick={() => open(n)}
                  onKeyDown={(e) => e.key === 'Enter' && open(n)}
                >
                  {n.kind === 'person' ? (
                    <circle cx={p.x} cy={p.y} r={n.r} className="person" />
                  ) : (
                    <rect
                      x={p.x - n.r}
                      y={p.y - n.r}
                      width={n.r * 2}
                      height={n.r * 2}
                      rx={3}
                      className={`project stage-${n.project.stage.toLowerCase()}`}
                    />
                  )}
                </g>
              )
            })}
            {/* Names last, so no shape is ever drawn on top of them. */}
            <g className="network-labels" aria-hidden="true">
              {graph.nodes.map((n) => {
                const p = graph.positions.get(n.id)
                if (!p || n.kind !== 'person') return null
                return (
                  <text key={n.id} x={p.x} y={p.y + n.r + 13} textAnchor="middle" className={isLit(n.id) ? '' : 'dim'}>
                    {n.label}
                  </text>
                )
              })}
            </g>
          </svg>
        )}
        <Tooltip tip={tip} />
      </div>
      <div className="chart-legend">
        <span><i className="swatch person-swatch" /> Stakeholder (size = number of projects)</span>
        <span><i className="swatch stage-lead" /> Lead</span>
        <span><i className="swatch stage-opportunity" /> Opportunity</span>
        <span><i className="swatch stage-tender" /> Tender</span>
        <span className="muted">Project size = ₹ value · hover to isolate connections</span>
      </div>
    </div>
  )
}
